import React, { createContext, useState, useContext, useEffect } from 'react';
import { noteChildLogin } from '../components/Sparks/sparksTips';
import Cookies from 'js-cookie';
import { renewLogin, verifyToken } from '../services/authService';
import { SESSION_EXPIRED_EVENT, SESSION_EXPIRED_LOGIN_PATH } from '../utils/sessionExpiry';
import { RENEW_CHECK_MS, shouldRenew } from '../utils/tokenRenewal';
import posthog from 'posthog-js';


const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loggedInChild, setLoggedInChild] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // Remove userId state

  const USER_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

  const fullLogout = () => {
    Cookies.remove('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userCachedAt');
    setIsAuthenticated(false);
    setUser(null);
    setAuthError(null);
    posthog.reset();
  };

  useEffect(() => {
    const verifyAuthentication = async () => {
      const token = Cookies.get('token');
      const storedUser = JSON.parse(localStorage.getItem('user'));
      if (token && storedUser) {
        try {
          const userData = await verifyToken(token);
          setUser(userData);
          setIsAuthenticated(true);
          setAuthError(null);

          posthog.identify(userData._id, {
            email: userData.email,
            name: userData.name,
            role: userData.role,
          });

          // Update localStorage with fresh data and timestamp
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userCachedAt', Date.now().toString());

          // Retrieve logged in child from localStorage
          const storedChild = localStorage.getItem('loggedInChild');
          if (storedChild) {
            const childData = JSON.parse(storedChild);
            setLoggedInChild(childData);
            posthog.identify(childData._id, {
              name: childData.name,
              yearGroup: childData.yearGroup,
              role: 'student',
            });
          }
        } catch (error) {
          // The API refused the token (expired or invalid): the session is over, so say so.
          if (error.response?.status === 401) {
            expireSession();
            return;
          }
          // Network or server trouble: fall back to cached user data if it's fresh enough
          const cachedAt = parseInt(localStorage.getItem('userCachedAt') || '0', 10);
          const cacheAge = Date.now() - cachedAt;

          if (cachedAt && cacheAge < USER_CACHE_MAX_AGE_MS) {
            // Cache is recent — trust it and continue the session
            setUser(storedUser);
            setIsAuthenticated(true);
            setAuthError(null);
            posthog.identify(storedUser._id, {
              email: storedUser.email,
              name: storedUser.name,
              role: storedUser.role,
            });
            const storedChild = localStorage.getItem('loggedInChild');
            if (storedChild) {
              setLoggedInChild(JSON.parse(storedChild));
            }
          } else {
            // Cache is stale or missing — force a clean logout
            console.error('verifyToken failed and cached user data is stale — logging out', error);
            fullLogout();
          }
        }
      } else {
        setAuthError(null);
      }
      setIsLoading(false);
    };

    verifyAuthentication();
  }, []);

  // Any API call the server refuses for the login itself (utils/api.js) ends the session here.
  useEffect(() => {
    const onExpired = () => {
      if (Cookies.get('token') || localStorage.getItem('user')) expireSession();
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // Tutors: while the app is open and on screen, swap the login for a fresh one about once a day,
  // so a class never runs into an expired login (utils/tokenRenewal.js).
  const isTutor = !!user?.isTutor;
  useEffect(() => {
    if (!isTutor) return undefined;
    let renewing = false;
    const check = async () => {
      if (renewing || document.visibilityState !== 'visible' || !shouldRenew(Cookies.get('token'), Date.now())) return;
      renewing = true;
      try {
        await renewLogin();
      } catch {
        // The current login is still valid; the next check tries again.
      } finally {
        renewing = false;
      }
    };
    check();
    const timer = setInterval(check, RENEW_CHECK_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [isTutor]);

  const loginChild = (childData) => {
    setLoggedInChild(childData);
    localStorage.setItem('loggedInChild', JSON.stringify(childData));
    noteChildLogin(childData._id);
    posthog.identify(childData._id, {
      name: childData.name,
      yearGroup: childData.yearGroup,
      role: 'student',
    });
  }

  const logOutChild = () => {
    setLoggedInChild(null);
    localStorage.removeItem('loggedInChild');
  };

  const login = (userData) => {

    // i've apparently got some duplication between this login and the login in authService.js
    const userToStore = {
      ...userData,
      _id: userData._id.toString(),
      accessStatus: userData.accessStatus
    };
    setUser(userToStore);
    setIsAuthenticated(true);
    setAuthError(null);
    localStorage.setItem('user', JSON.stringify(userToStore));
    localStorage.setItem('userCachedAt', Date.now().toString());

    posthog.identify(userToStore._id, {
      email: userToStore.email,
      name: userToStore.name,
      role: userToStore.role,
    });
  };

  const logout = () => {
    Cookies.remove('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userCachedAt');
    logOutChild();
    setIsAuthenticated(false);
    setUser(null);
    setAuthError(null);
    posthog.reset();
  };

  // Clear the dead login and reload onto the login page with a note, so nobody keeps working on a
  // page that only looks logged in. The full load also ends any live call cleanly.
  const expireSession = () => {
    logout();
    window.location.assign(SESSION_EXPIRED_LOGIN_PATH);
  };

  const setChildLogin = (childData) => {
    setLoggedInChild(childData);
    localStorage.setItem('loggedInChild', JSON.stringify(childData));
  };

  const updateLoggedInChild = (updatedChildData) => {
    setLoggedInChild(updatedChildData);
    localStorage.setItem('loggedInChild', JSON.stringify(updatedChildData));
  }

  const childLogout = () => {
    setLoggedInChild(null);
    localStorage.removeItem('loggedInChild');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user,
      loginChild,
      logOutChild,
      login, 
      logout, 
      authError, 
      setAuthError,
      loggedInChild,
      setChildLogin,
      childLogout,
      updateLoggedInChild
    }}>
      {children} 
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { AuthContext };